import { Box, Center, ChakraProvider, CircularProgress, extendTheme, Fade, Image } from '@chakra-ui/react';
import { IAgreement, ISubscription } from '@partiu/api/dashboard/types.ts';
import { BookstoreLabels } from '@partiu/api/landing/utils.ts';
import { IBanner } from '@partiu/api/types';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LoadingScreen } from '../components/organisms/LoadingScreen.tsx';
import { DialogProvider } from './DialogProvider.tsx';
import { Helmet } from 'react-helmet';
import { axiosInstance, axiosInstanceV2, axiosInstanceV3, isAuthenticated } from '@partiu/api/axios.ts';
import { bookstoreSlugResolver } from '../resolvers/slugResolvers.ts';

interface IColors {
  primaryColor: string;
  primaryFontColor: string;
  secondaryColor: string;
  secondaryFontColor: string;
  menuColor: string;
  menuFontColor: string;
  graphColors?: string[];
}

interface IThemeContext {
  id: number | null;
  name: string;
  logo: string;
  slug: string;
  exClientModule: boolean;
  cellphone: string;
  isNative: boolean;
  showBestSellers: boolean;
  product: any;
  colors: IColors;
  banners: IBanner[];
  extraData: Record<string, string | number>;
  email: string;
  telephone: string;
  isSetup: boolean;
  privacyPolicy?: string;
  legalText?: string;
  uraModuleCellphone: string;
  bookstoreLabels: BookstoreLabels;
  bookstoreTypes: string[];
  digitalMagazinesModule?: boolean;
  recurringBook: boolean;
  // Permissions
  allowCpfLoginMethod: boolean;
  allowPromocodeLoginMethod: boolean;

  // Login
  integratedLogin: string;
  idp: string;
  allowSignup: boolean;
  cpfModule: boolean;

  // Menu variables for dashboards
  isMenuOpen: boolean;
  metadata: Record<string, string | boolean>;
  // URL data
  isApp: boolean;
  isIOS: boolean;

  // Update functions
  setLoading: (value: boolean, description?: string, delay?: number) => void;
  setMenuOpen: (value: boolean) => void;
  updateTheme: (data: IThemeContextUpdate) => void;
  resetTheme: () => void;
  isLogged: boolean;
  getValidSubscriptions: (subscriptions: ISubscription[]) => ISubscription[];
  reloadTheme: () => void;
}

interface ILoadingContext {
  isLoading: boolean;
  loadingDelay: number;
  loadingDescription: string;
}

export interface IThemeContextUpdate {
  id?: number | null;
  name?: string;
  logo?: string;
  colors?: IColors;
  exClientModule?: boolean;
  isLoading?: boolean;
  loadingDescription?: string;
  banners?: IBanner[];
  email?: string;
  telephone?: string;
  bookstoreLabels: BookstoreLabels;
  uraModuleCellphone?: string;
  bookstoreTypes: string[];
  // Permissions
  allowCpfLoginMethod: boolean;
  allowPromocodeLoginMethod: boolean;

  // Menu variables for dashboards
  isMenuOpen?: boolean;
  // Login
  integratedLogin?: string;
  showBestSellers?: boolean;
  idp: string;
  allowSignup: boolean;
  cpfModule: boolean;
  metadata?: Record<string, string | boolean>;
}

const defaultThemeContextData: IThemeContext = {
  exClientModule: false,
  id: null,
  name: '',
  cellphone: '',
  isNative: false,
  logo: '',
  extraData: {},
  email: 'suporte@partiu.com.br',
  telephone: '(21) 97420-4416',
  colors: {
    primaryColor: '#ffffff',
    primaryFontColor: '#ffffff',
    secondaryColor: '#ffffff',
    secondaryFontColor: '#ffffff',
    menuColor: '#ffffff',
    menuFontColor: '#ffffff',
    graphColors: [],
  },
  banners: [],
  uraModuleCellphone: '',
  showBestSellers: false,
  bookstoreLabels: {
    singular: 'Ebook',
    plural: 'Ebooks',
    prefixedSingular: 'o',
    prefixedPlural: 'os',
    prefix: 'o',
    prefixPlural: 'os',
  },
  bookstoreTypes: [],
  recurringBook: true,
  // Menu variables for dashboards
  isMenuOpen: false,

  // Update functions
  setMenuOpen: () => {},
  updateTheme: () => {},
  resetTheme: () => {},
  setLoading: () => {},

  // Login
  integratedLogin: '',
  cpfModule: false,
  idp: '',
  allowSignup: true,
  metadata: {},
  isNative: true,
};

const ThemeContext = createContext(defaultThemeContextData);

export function ThemeProvider({
  children,
  themeResolver,
}: { themeResolver: () => Promise<IAgreement> } & PropsWithChildren) {
  const [setupData, setSetupData] = useState({
    isSetup: false,
    animate: false,
    exit: true,
  });
  const isLogged = Boolean(isAuthenticated());
  const [urlData, setUrlData] = useState({ isApp: false, isIOS: false });
  const [data, setData] = useState<IThemeContext>(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('theme') || 'null');
      if (cached) return { ...defaultThemeContextData, ...cached };
    } catch {
      // ignore corrupted cache
    }
    return defaultThemeContextData;
  });
  const [isThemeLoaded, setIsThemeLoaded] = useState(() => {
    try {
      return Boolean(localStorage.getItem('theme')) && Boolean(localStorage.getItem('agreementId'));
    } catch {
      return false;
    }
  });

  const [loadingData, setLoadingData] = useState<ILoadingContext>({
    isLoading: false,
    loadingDelay: 0,
    loadingDescription: 'Carregando...',
  });

  const theme = useMemo(
    () =>
      extendTheme({
        colors: {
          ...data.colors,
        },
      }),
    [data]
  );
  const updateTheme = useCallback(
    (newThemeData: IThemeContextUpdate) => {
      setData({ ...data, ...newThemeData });
    },
    [data, setData]
  );

  function getQueryParam(name: string) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  const isNative = getQueryParam('isNative') === 'true';

  const reloadTheme = useCallback(() => {
    bookstoreSlugResolver().then((agreement: IThemeContextUpdate) => {
      setData({ ...data, ...agreement });
      localStorage.setItem('theme', JSON.stringify(agreement));
    });
  }, [data]);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    const urlData = {
      isApp: params.isApp === 'true',
      isIOS: params.isIOS === 'true',
    };
    const existingUrlData = JSON.parse(localStorage.getItem('urlData') || 'null');

    if (existingUrlData) {
      if (!params.isApp) urlData['isApp'] = existingUrlData['isApp'];
      if (!params.isIOS) urlData['isIOS'] = existingUrlData['isIOS'];
    }

    localStorage.setItem('urlData', JSON.stringify(urlData));

    setUrlData(urlData);
  }, []);

  useEffect(() => {
    const skipSplash = isNative || window.location.pathname.includes('/legal/');

    if (!skipSplash) {
      setSetupData((prev) => ({ ...prev, isSetup: false, animate: false, exit: false }));
    }

    themeResolver()
      .then((agreement: IAgreement) => {
        setData((prev) => ({ ...prev, ...agreement }));
        localStorage.setItem('theme', JSON.stringify(agreement));
        if (agreement.id) {
          const agreementId = agreement.id.toString();
          axiosInstance.defaults.headers['x-agreement-id'] = agreementId;
          axiosInstanceV2.defaults.headers['x-agreement-id'] = agreementId;
          axiosInstanceV3.defaults.headers['x-agreement-id'] = agreementId;
          localStorage.setItem('agreementId', agreementId);
        }

        setIsThemeLoaded(true);

        if (skipSplash) {
          (window as any).__dismissInitialLoader?.();
          return;
        }

        setTimeout(() => setSetupData((prev) => ({ ...prev, animate: true })), 2000);
        setTimeout(() => setSetupData((prev) => ({ ...prev, exit: true })), 3000);
      })
      .catch(() => {});
  }, [themeResolver, isNative]);

  return (
    <ThemeContext.Provider
      value={{
        ...data,
        ...urlData,
        isLogged,
        isNative,
        reloadTheme,
        setLoading: (value: boolean, description: string = '', delay: number = loadingData.loadingDelay) => {
          setLoadingData({ isLoading: value, loadingDescription: description, loadingDelay: delay });
        },
        setMenuOpen: (value: boolean) => setData({ ...data, isMenuOpen: value }),
        updateTheme,
        resetTheme: () => setData(defaultThemeContextData),
        getValidSubscriptions: (subscriptions: ISubscription[]) => {
          return subscriptions
            .filter((sub) => sub.plan.agreement.id === data.id)
            .sort((a, b) => a.plan.level - b.plan.level);
        },
      }}
    >
      <Helmet>
        <meta charSet="utf-8" />
        <title>{data.name}</title>
      </Helmet>
      <ChakraProvider theme={theme}>
        <DialogProvider>
          <LoadingScreen
            description={loadingData.loadingDescription}
            isOpen={loadingData.isLoading}
            loadingDelay={loadingData.loadingDelay}
          />
          {!isNative && (
            <>
              {!setupData.exit && (
                <Fade
                  unmountOnExit={true}
                  transition={{ exit: { duration: 0.2 }, enter: { duration: 0 } }}
                  in={!data.logo}
                >
                  <Center
                    alignContent={'center'}
                    justifyContent={'center'}
                    pos={'fixed'}
                    left={'0px'}
                    top={'0px'}
                    width="100%"
                    height="100vh"
                    pointerEvents={'none'}
                  >
                    <CircularProgress margin="auto" transition={'0.1s'} color="black" isIndeterminate />
                  </Center>
                </Fade>
              )}
              <Fade
                transition={{ exit: { duration: 0.5 }, enter: { duration: 1, delay: 0.5 } }}
                in={!!data.logo && !setupData.exit}
              >
                <Center
                  alignContent={'center'}
                  justifyContent={'center'}
                  pos={'fixed'}
                  left={'0px'}
                  top={'0px'}
                  width="100%"
                  height="100vh"
                  pointerEvents={'none'}
                  zIndex={1000}
                >
                  <Box
                    width={'50px'}
                    height={'50px'}
                    borderRadius={'100%'}
                    pos={'absolute'}
                    backgroundColor={data.colors.primaryColor}
                    transition={'2s'}
                    transform={setupData.animate ? 'scale(100)' : 'scale(0)'}
                  />
                  <Image maxWidth={'500px'} src={data.logo} w={'70%'} margin="auto" transition={'0.1s'} />
                </Center>
              </Fade>
            </>
          )}
          {isThemeLoaded && (
            <Fade in={isNative || setupData.exit} transition={{ enter: { duration: 0.2, delay: 0 } }}>
              {children}
            </Fade>
          )}
        </DialogProvider>
      </ChakraProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
